import { useMemo } from "react";
import type {
  TransitionPresentationComponentProps,
  TransitionPresentation,
} from "@remotion/transitions";
import { AbsoluteFill, interpolate } from "remotion";

export type CubeDirection =
  | "from-left"
  | "from-right"
  | "from-top"
  | "from-bottom";

type CubePresentationProps = {
  direction: CubeDirection;
  perspective?: number;
};

const Cube: React.FC<
  TransitionPresentationComponentProps<CubePresentationProps>
> = ({
  children,
  presentationDirection,
  presentationProgress,
  passedProps: { perspective = 1000, ...passedProps },
}) => {
  // eslint-disable-next-line complexity
  const style: React.CSSProperties = useMemo(() => {
    const startRotationEntering =
      passedProps.direction === "from-right" ||
      passedProps.direction === "from-top"
        ? 90
        : -90;
    const endRotationEntering =
      passedProps.direction === "from-right" ||
      passedProps.direction === "from-top"
        ? -90
        : 90;

    const startPositionEntering =
      passedProps.direction === "from-right" ||
      passedProps.direction === "from-bottom"
        ? 100
        : -100;
    const startPositionExiting =
      passedProps.direction === "from-right" ||
      passedProps.direction === "from-bottom"
        ? -100
        : 100;

    const transformOriginEntering =
      passedProps.direction === "from-left"
        ? "right"
        : passedProps.direction === "from-bottom"
        ? "top"
        : passedProps.direction === "from-top"
        ? "bottom"
        : "left";
    const transformOriginExiting =
      passedProps.direction === "from-left"
        ? "left"
        : passedProps.direction === "from-bottom"
        ? "bottom "
        : passedProps.direction === "from-top"
        ? "top"
        : "right";

    const rotation =
      presentationDirection === "entering"
        ? interpolate(presentationProgress, [0, 1], [startRotationEntering, 0])
        : interpolate(presentationProgress, [0, 1], [0, endRotationEntering]);

    const transformOrigin =
      presentationDirection === "entering"
        ? transformOriginEntering
        : transformOriginExiting;

    const translate = `${
      presentationDirection === "entering"
        ? interpolate(presentationProgress, [0, 1], [startPositionEntering, 0])
        : interpolate(presentationProgress, [0, 1], [0, startPositionExiting])
    }%`;

    const translateProperty =
      passedProps.direction === "from-top" ||
      passedProps.direction === "from-bottom"
        ? "translateY"
        : "translateX";
    const rotateProperty =
      passedProps.direction === "from-top" ||
      passedProps.direction === "from-bottom"
        ? "rotateX"
        : "rotateY";

    return {
      width: "100%",
      height: "100%",
      transformOrigin,
      transform: `${translateProperty}(${translate}) ${rotateProperty}(${rotation}deg)`,
      backfaceVisibility: "hidden",
      WebkitBackfaceVisibility: "hidden",
    };
  }, [passedProps.direction, presentationDirection, presentationProgress]);

  return (
    <AbsoluteFill
      style={{
        perspective,
        transformStyle: "preserve-3d",
      }}
    >
      <AbsoluteFill style={style}>{children}</AbsoluteFill>
    </AbsoluteFill>
  );
};

export const cube = (
  props: CubePresentationProps
): TransitionPresentation<CubePresentationProps> => {
  return { component: Cube, props };
};

;
    var _remotion_globalVariableA, _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
